// UserAvatar.js - Avatar Component mit Dropdown
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

function UserAvatar({ token, onLogout }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadProfile();
    
    // Close dropdown on outside click
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [token]);

  const loadProfile = async () => {
    try {
      const response = await fetch('/api/users/profile', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setProfile(data.user);
      }
    } catch (error) {
      console.error('Load profile error:', error);
    }
  };

  const getInitials = () => {
    if (!profile?.username) return '?';
    return profile.username[0].toUpperCase();
  };

  const getAvatarUrl = () => {
    return profile?.avatar_url || null;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold hover:shadow-lg hover:shadow-cyan-500/50 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
      >
        {getAvatarUrl() ? (
          <img 
            src={getAvatarUrl()} 
            alt="Avatar" 
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          <span className="text-lg">{getInitials()}</span>
        )}
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-64 bg-slate-900 border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-white/10">
            <p className="font-semibold text-white truncate">{profile?.username}</p>
            <p className="text-xs text-slate-400 truncate">{profile?.email}</p>
          </div>

          {/* Storage Info */}
          <div className="px-4 py-3 border-b border-white/10">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>Speicher</span>
              <span>
                {((profile?.storage_used || 0) / 1024 / 1024 / 1024).toFixed(1)} GB / 
                {((profile?.storage_limit || 0) / 1024 / 1024 / 1024).toFixed(0)} GB
              </span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-all"
                style={{ 
                  width: `${Math.min(100, (profile?.storage_used || 0) / (profile?.storage_limit || 1) * 100)}%` 
                }}
              ></div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <button
              onClick={() => {
                setShowDropdown(false);
                navigate('/settings');
              }}
              className="w-full px-4 py-2 text-left text-white hover:bg-white/10 transition-all flex items-center gap-3"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Einstellungen</span>
            </button>

            <button
              onClick={() => {
                setShowDropdown(false);
                onLogout();
              }}
              className="w-full px-4 py-2 text-left text-red-400 hover:bg-red-500/10 transition-all flex items-center gap-3"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Abmelden</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserAvatar;
